import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "vbb_admin_key";
const HTTP_UNAUTHORIZED = 401;
const IS_DEV = process.env.NODE_ENV !== "production";

function devWarn(...args) {
  if (IS_DEV) console.warn(...args);
}

/**
 * useAdminEssays — CRUD hook for /admin/essays. Shares the same
 * sessionStorage admin key as the rest of the admin surface.
 */
export default function useAdminEssays() {
  const [key] = useState(() => sessionStorage.getItem(KEY_STORAGE) || "");
  const [essays, setEssays] = useState([]);
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const authHeaders = () => ({ "X-Admin-Key": key });

  const fetchEssays = useCallback(async () => {
    if (!key) return;
    setStatus({ state: "loading", error: "" });
    try {
      const res = await axios.get(`${API}/admin/essays`, {
        headers: authHeaders(),
      });
      setEssays(res.data || []);
      setStatus({ state: "ready", error: "" });
    } catch (err) {
      if (err?.response?.status === HTTP_UNAUTHORIZED) {
        setStatus({ state: "unauthorized", error: "Session expired." });
      } else {
        setStatus({ state: "error", error: "Could not load essays." });
      }
    }
  }, [key]);

  useEffect(() => {
    if (key) fetchEssays();
  }, [key, fetchEssays]);

  const createEssay = useCallback(
    async (payload) => {
      const res = await axios.post(`${API}/admin/essays`, payload, {
        headers: authHeaders(),
      });
      setEssays((prev) => [res.data, ...prev]);
      return res.data;
    },
    [key]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  const getEssay = useCallback(
    async (id) => {
      const res = await axios.get(`${API}/admin/essays/${id}`, {
        headers: authHeaders(),
      });
      return res.data;
    },
    [key]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  const patchEssay = useCallback(
    async (id, patch) => {
      const res = await axios.patch(`${API}/admin/essays/${id}`, patch, {
        headers: authHeaders(),
      });
      setEssays((prev) => prev.map((e) => (e.id === id ? res.data : e)));
      return res.data;
    },
    [key]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  const deleteEssay = useCallback(
    async (id) => {
      try {
        await axios.delete(`${API}/admin/essays/${id}`, {
          headers: authHeaders(),
        });
        setEssays((prev) => prev.filter((e) => e.id !== id));
        return true;
      } catch (err) {
        devWarn("[VBB] deleteEssay failed", err);
        return false;
      }
    },
    [key]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {
    key,
    essays,
    status,
    fetchEssays,
    createEssay,
    getEssay,
    patchEssay,
    deleteEssay,
  };
}
